import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, List, ListItem, ListItemButton, ListItemText,
  Chip, Alert, InputAdornment, Tabs, Tab, CircularProgress,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TireScanner from './TireScanner';
import TireForm from './TireForm';
import {
  getTires, findTireByBarcode, TIRE_STATUSES, isNearEndOfLife,
} from '../../utils/tiresService';

export default function TirePicker({ open, onClose, onSelect, position, teamId, userId, vehicleId, vehicles = [], excludeTireIds = [] }) {
  const { t } = useTranslation('tires');
  const [tab, setTab]           = useState(0);
  const [tires, setTires]       = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const [scannerOpen, setScannerOpen] = useState(false);
  const [formOpen, setFormOpen]       = useState(false);
  const [prefillBarcode, setPrefillBarcode] = useState('');

  useEffect(() => { setError(''); }, [tab]);

  useEffect(() => {
    if (!open || !teamId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    getTires(teamId)
      .then(list => { if (!cancelled) setTires(list); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, teamId]);

  const excludeSet = useMemo(() => new Set(excludeTireIds), [excludeTireIds]);
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return tires
      .filter(tr => {
        if (tr.status === 'scrapped') return false;
        if (excludeSet.has(tr.id)) return false;
        if (vehicleId && tr.vehicleId && tr.vehicleId !== vehicleId) return false;
        if (s) {
          const hay = `${tr.barcode} ${tr.brand || ''} ${tr.compound || ''} ${tr.dotCode || ''}`.toLowerCase();
          if (!hay.includes(s)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (a.position === position && b.position !== position) return -1;
        if (b.position === position && a.position !== position) return 1;
        const sa = a.status === 'available' || a.status === 'new' ? 0 : 1;
        const sb = b.status === 'available' || b.status === 'new' ? 0 : 1;
        if (sa !== sb) return sa - sb;
        return 0;
      });
  }, [tires, search, vehicleId, position, excludeSet]);

  const handleScanResult = async (code) => {
    setScannerOpen(false);
    const trimmed = code.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      const tire = await findTireByBarcode(teamId, trimmed);
      if (tire) {
        if (excludeSet.has(tire.id)) {
          const descr = `${tire.brand || ''} ${tire.compound || ''}`.trim();
          setError(t('picker_block_already_assigned', { descr, barcode: tire.barcode }));
          setTab(0);
          return;
        }
        onSelect(tire);
        onClose();
      } else {
        setPrefillBarcode(trimmed);
        setFormOpen(true);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const statusChip = (s) => {
    const cfg = TIRE_STATUSES.find(x => x.key === s) || { color: '#999' };
    return <Chip label={t(`status_${s}`, s)} size="small" sx={{ bgcolor: cfg.color, color: '#fff', fontWeight: 600, fontSize: 10, height: 18 }} />;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('picker_title')} <strong style={{ color: '#c62828' }}>{position}</strong>
        </DialogTitle>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={t('picker_tab_db')} />
          <Tab label={t('picker_tab_scan')} />
        </Tabs>

        <DialogContent sx={{ p: 0 }}>
          {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

          {tab === 0 && (
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth size="small"
                placeholder={t('picker_search_placeholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ mb: 1 }}
              />
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
              ) : filtered.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  {tires.length === 0 ? t('picker_empty_none') : t('picker_empty_no_match')}
                </Typography>
              ) : (
                <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
                  {filtered.map(tr => {
                    const nearEol = isNearEndOfLife(tr);
                    const dotSuffix = tr.dotCode ? t('picker_dot_suffix', { dot: tr.dotCode }) : '';
                    return (
                      <ListItem key={tr.id} disablePadding divider>
                        <ListItemButton onClick={() => { onSelect(tr); onClose(); }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {tr.brand || '—'} {tr.compound && `· ${tr.compound}`}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                  {tr.barcode}
                                </Typography>
                                {statusChip(tr.status)}
                                {tr.position && (
                                  <Chip label={`@ ${tr.position}`} size="small" variant="outlined" sx={{ fontSize: 9, height: 16 }} />
                                )}
                                {nearEol && <Chip label={t('picker_eol_chip')} size="small" color="warning" sx={{ fontSize: 9, height: 16 }} />}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {t('picker_secondary', { km: tr.kmTotal, cycles: tr.heatCycles, dot: dotSuffix })}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center' }}
                dangerouslySetInnerHTML={{ __html: t('picker_scan_help') }}
              />
              <Button variant="contained" size="large" startIcon={<QrCodeScannerIcon />} onClick={() => setScannerOpen(true)}>
                {t('picker_button_scan')}
              </Button>
              <Typography variant="caption" color="text.disabled">{t('picker_or')}</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setPrefillBarcode(''); setFormOpen(true); }}>
                {t('picker_button_manual')}
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>

      <TireScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onCapture={handleScanResult}
      />

      <TireForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={async () => {
          if (prefillBarcode) {
            const tire = await findTireByBarcode(teamId, prefillBarcode);
            if (tire) { onSelect(tire); onClose(); }
          } else {
            const list = await getTires(teamId);
            setTires(list);
          }
        }}
        tire={prefillBarcode ? { barcode: prefillBarcode, vehicleId: vehicleId || null, position } : null}
        teamId={teamId}
        userId={userId}
        vehicles={vehicles}
      />
    </>
  );
}
